import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bath,
  Bed,
  BedDouble,
  Bell,
  Brain,
  Briefcase,
  Bus,
  Camera,
  Crown,
  DollarSign,
  Dumbbell,
  Facebook,
  Flame,
  Globe,
  Heart,
  Home,
  ImagePlus,
  Instagram,
  Lightbulb,
  ListChecks,
  Lock,
  MapPin,
  Music,
  PartyPopper,
  Settings,
  ShieldCheck,
  Shirt,
  Stethoscope,
  Tv,
  Users,
  Wifi,
  X,
  Youtube
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import ProviderGallery from '@/components/ProviderGallery';
import PremiumGallery from '@/components/PremiumGallery';
import {
  PET_AMENITY_CATEGORIES,
  PET_AMENITY_ICON_MAP,
  formatPetAmenity,
} from '@/config/petAmenities';


const SERVICE_CATEGORIES = [
  { key: 'residencias', label: 'Residencias', icon: Home, desc: 'Estadía permanente con cuidado integral' },
  { key: 'cuidado-domicilio', label: 'Cuidado a Domicilio', icon: Heart, desc: 'Atención profesional en el hogar' },
  { key: 'salud-mental', label: 'Salud Mental', icon: Brain, desc: 'Apoyo psicológico y terapias' },
];

const AMENITY_ICON_MAP = PET_AMENITY_ICON_MAP;
const AMENITY_CATEGORIES = PET_AMENITY_CATEGORIES;
const formatAmenity = formatPetAmenity;

const ProviderAccount = () => {
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  // Profile
  const [profileForm, setProfileForm] = useState({ business_name: '', description: '', phone: '', address: '', municipio: '', region: '', website: '', youtube_video_url: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingProfilePhoto, setUploadingProfilePhoto] = useState(false);

  // Pricing
  const [pricing, setPricing] = useState({
    residencias: { price_from: '', description: '' },
    'cuidado-domicilio': { price_from: '', description: '' },
    'salud-mental': { price_from: '', description: '' },
  });
  const [savingPricing, setSavingPricing] = useState(false);

  // Amenities
  const [amenities, setAmenities] = useState([]);
  const [savingAmenities, setSavingAmenities] = useState(false);

  // Social
  const [social, setSocial] = useState({ instagram: '', facebook: '', website: '' });
  const [savingSocial, setSavingSocial] = useState(false);

  // Cuidados y Estadía
  const [careTypes, setCareTypes] = useState([]);
  const [stayTypes, setStayTypes] = useState([]);
  const [savingCare, setSavingCare] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/providers/my-profile');
      const p = res.data;
      setProvider(p);
      setProfileForm({
        business_name: p.business_name || '',
        description: p.description || '',
        phone: p.phone || '',
        address: p.address || '',
        municipio: p.municipio || '',
        region: p.region || '',
        website: p.social_links?.website || p.website || '',
        youtube_video_url: p.youtube_video_url || '',
      });
      // Load pricing from services
      const pricingObj = { residencias: { price_from: '', description: '', sub_prices: [] }, 'cuidado-domicilio': { price_from: '', description: '', sub_prices: [] }, 'salud-mental': { price_from: '', description: '', sub_prices: [] } };
      (p.services || []).forEach(svc => {
        if (pricingObj[svc.service_type] !== undefined) {
          pricingObj[svc.service_type] = { price_from: svc.price_from || '', description: svc.description || '', sub_prices: svc.sub_prices || [] };
        }
      });
      setPricing(pricingObj);
      setAmenities(p.amenities || []);
      setCareTypes(p.care_types || []);
      setStayTypes(p.stay_types || []);
      setSocial({
        instagram: p.social_links?.instagram || '',
        facebook: p.social_links?.facebook || '',
        website: p.social_links?.website || p.website || '',
      });
    } catch (error) {
      if (error.response?.status === 401) navigate('/login');
      else if (error.response?.status === 404) navigate('/registrar-residencia');
    } finally { setLoading(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put('/providers/my-profile', profileForm);
      toast.success('Perfil actualizado');
      setProvider(prev => ({ ...prev, ...profileForm }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
    finally { setSavingProfile(false); }
  };

  const uploadProfilePhoto = async (file) => {
    setUploadingProfilePhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/providers/my-profile/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProvider(prev => ({ ...prev, profile_photo: res.data.photo_url }));
      toast.success('Foto actualizada');
    } catch { toast.error('Error al subir foto'); }
    finally { setUploadingProfilePhoto(false); }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      const svcArray = Object.entries(pricing).map(([type, data]) => ({
        service_type: type, price_from: parseInt(data.price_from) || 0, description: data.description || '',
        sub_prices: (data.sub_prices || []).filter(sp => sp.name)
      })).filter(s => s.price_from > 0 || s.description);
      await api.put('/providers/my-profile/services', { services: svcArray });
      toast.success('Precios actualizados');
    } catch { toast.error('Error al guardar precios'); }
    finally { setSavingPricing(false); }
  };

  const toggleAmenity = (item) => {
    setAmenities(prev => prev.includes(item) ? prev.filter(a => a !== item) : [...prev, item]);
  };

  const saveAmenities = async () => {
    setSavingAmenities(true);
    try {
      await api.put('/providers/my-profile/amenities', { amenities });
      toast.success('Servicios actualizados');
    } catch { toast.error('Error al guardar servicios'); }
    finally { setSavingAmenities(false); }
  };

  const saveSocial = async () => {
    setSavingSocial(true);
    try {
      await api.put('/providers/my-profile/social', social);
      toast.success('Redes sociales actualizadas');
    } catch { toast.error('Error al guardar'); }
    finally { setSavingSocial(false); }
  };

  const saveCareStayTypes = async () => {
    setSavingCare(true);
    try {
      await api.put('/providers/my-profile', { care_types: careTypes, stay_types: stayTypes });
      setProvider(prev => ({ ...prev, care_types: careTypes, stay_types: stayTypes }));
      toast.success('Cuidados y estadía actualizados');
    } catch { toast.error('Error al guardar'); }
    finally { setSavingCare(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" /></div>;
  if (!provider) return <div className="min-h-screen flex items-center justify-center"><p className="text-gray-500">No tienes un perfil de proveedor</p></div>;

  const plan = provider.plan_active ? (provider.plan_type || '') : '';
  
  // Permission map per plan
  const canEdit = {
    profile: !!plan, // sobre mí, más info
    pricing: !!plan, // precios
    gallery: !!plan, // galería
    amenities: plan === 'premium' || plan === 'premium_plus',
    social: plan === 'premium_plus',
    youtube: plan === 'premium_plus',
  };

  // Filter visible tabs based on plan
  const allTabs = [
    { key: 'profile', label: 'Perfil del Servicio', icon: Settings },
    { key: 'pricing', label: 'Precios', icon: DollarSign },
    { key: 'amenities', label: 'Servicios', icon: ListChecks },
    { key: 'gallery', label: 'Galería', icon: Camera },
    { key: 'social', label: 'Redes Sociales', icon: Globe },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-6 text-[#000000]">Mi Cuenta</h1>

        {/* Plan info */}
        {!plan && (
          <div className="mb-4 p-4 bg-gray-50 border rounded-xl">
            <p className="text-sm text-gray-600">No tienes un plan activo. Para acceder a funciones de edición, contacta a <a href="mailto:hola@petadvisor.es" className="text-[#ffff00] underline font-medium">hola@petadvisor.es</a></p>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 overflow-x-auto pb-2">
          <div className="flex gap-2 min-w-max">
            {allTabs.map(({ key, label, icon: Icon }) => {
              const allowed = canEdit[key];
              const isActive = activeTab === key;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => allowed && setActiveTab(key)}
                  disabled={!allowed}
                  data-testid={`tab-${key}`}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-bold whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-[#000000] border-[#000000] text-white shadow-sm'
                      : allowed
                        ? 'bg-white border-gray-200 text-[#000000] hover:border-[#000000] hover:bg-gray-50'
                        : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{label}</span>

                  {!allowed && (
                    <span className="text-[10px] bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">
                      Bloqueado
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Mi Perfil */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold mb-1 text-[#000000]">Editar Perfil del Servicio</h2>
            <p className="text-sm text-gray-500 mb-6">Esta información aparece en tu perfil público.</p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Foto del Servicio</label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-gray-200">
                    {provider.profile_photo ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${provider.profile_photo}`} alt="Perfil" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-3xl font-bold text-[#ffff00]">{provider.business_name?.[0]?.toUpperCase() || 'R'}</span>
                    )}
                  </div>
                  <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#ffff00] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#e6e600] transition-colors shadow-lg">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadProfilePhoto(e.target.files[0])} disabled={uploadingProfilePhoto} />
                    {uploadingProfilePhoto ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Camera className="w-4 h-4 text-white" />}
                  </label>
                </div>
                <p className="text-sm text-gray-500">Haz clic en el icono para cambiar la foto.</p>
              </div>
            </div>

            <form onSubmit={saveProfile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Servicio</label>
                <Input value={profileForm.business_name} onChange={e => setProfileForm(prev => ({ ...prev, business_name: e.target.value }))} placeholder="Ej: Clínica Veterinaria Patitas" data-testid="profile-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea value={profileForm.description} onChange={e => setProfileForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Describe tu servicio, experiencia y especialidades..." rows={4} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#ffff00] focus:border-transparent" data-testid="profile-description" />
              </div>

              {/* YouTube Video - Only for subscribed */}
              {canEdit.youtube ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-red-500" />
                    Video de YouTube
                    <span className="text-xs text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><Crown className="w-3 h-3" /> Premium</span>
                  </label>
                  <Input
                    value={profileForm.youtube_video_url}
                    onChange={e => setProfileForm(prev => ({ ...prev, youtube_video_url: e.target.value }))}
                    placeholder="https://www.youtube.com/watch?v=..."
                    data-testid="profile-youtube-url"
                  />
                  <p className="text-xs text-gray-400 mt-1">Pega la URL de tu video de YouTube. Aparecerá en tu perfil público.</p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3 border border-gray-200">
                  <Lock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Video de YouTube</p>
                    <p className="text-xs text-gray-400">Plan Premium+ requerido. Contacta a <a href="mailto:hola@petadvisor.es" className="text-[#ffff00] underline">hola@petadvisor.es</a></p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <Input value={profileForm.phone} onChange={e => setProfileForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="+34 600 000 000" data-testid="profile-phone" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Municipio</label>
                  <Input value={profileForm.municipio} onChange={e => setProfileForm(prev => ({ ...prev, municipio: e.target.value }))} placeholder="Madrid" data-testid="profile-municipio" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                  <Input value={profileForm.address} onChange={e => setProfileForm(prev => ({ ...prev, address: e.target.value }))} placeholder="Calle Principal 123" data-testid="profile-address" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comunidad autónoma</label>
                  <Input value={profileForm.region} onChange={e => setProfileForm(prev => ({ ...prev, region: e.target.value }))} placeholder="Comunidad autónoma Comunidad de Madrid" data-testid="profile-region" />
                </div>
              </div>
              <Button type="submit" disabled={savingProfile} className="bg-[#ffff00] hover:bg-[#e6e600] text-[#000000]" data-testid="save-profile-btn">
                {savingProfile ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </form>
          </div>
        )}

        {/* Precios */}
        {activeTab === 'pricing' && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold mb-1 text-[#000000]">Precios por Categoría</h2>
            <p className="text-sm text-gray-500 mb-6">Solo las categorías con precio aparecerán en tu perfil público.</p>
            <div className="space-y-4">
              {SERVICE_CATEGORIES.map(({ key, label, icon: Icon, desc }) => (
                <div key={key} className="p-4 bg-gray-50 rounded-xl border-2 border-gray-100 hover:border-[#ffff00]/30 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#ffff00]/10 flex items-center justify-center"><Icon className="w-5 h-5 text-[#ffff00]" /></div>
                    <div>
                      <span className="font-bold text-[#000000]">{label}</span>
                      <p className="text-xs text-gray-400">{desc}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Precio desde (€)</label>
                      <Input type="number" value={pricing[key].price_from} onChange={e => setPricing(prev => ({ ...prev, [key]: { ...prev[key], price_from: e.target.value } }))} placeholder="Ej: 50" data-testid={`price-${key}`} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Descripcion</label>
                      <Input value={pricing[key].description} onChange={e => setPricing(prev => ({ ...prev, [key]: { ...prev[key], description: e.target.value } }))} placeholder="Ej: Incluye alimentacion" data-testid={`desc-${key}`} />
                    </div>
                  </div>
                  {/* Sub-prices */}
                  {canEdit.youtube && (
                    <div className="mt-3 ml-2 border-l-2 border-[#ffff00]/30 pl-3 space-y-2">
                      <p className="text-xs font-semibold text-gray-500">Sub-precios (detalles del servicio)</p>
                      {(pricing[key].sub_prices || []).map((sp, j) => (
                        <div key={j} className="flex items-center gap-2" data-testid={`sub-price-${key}-${j}`}>
                          <Input
                            type="text"
                            placeholder="Nombre (ej: Hab. individual)"
                            value={sp.name || ''}
                            onChange={e => {
                              const subs = [...(pricing[key].sub_prices || [])];
                              subs[j] = { ...subs[j], name: e.target.value };
                              setPricing(prev => ({ ...prev, [key]: { ...prev[key], sub_prices: subs } }));
                            }}
                            className="flex-1 text-sm"
                          />
                          <Input
                            type="number"
                            placeholder="Precio"
                            value={sp.price || ''}
                            onChange={e => {
                              const subs = [...(pricing[key].sub_prices || [])];
                              subs[j] = { ...subs[j], price: parseInt(e.target.value) || 0 };
                              setPricing(prev => ({ ...prev, [key]: { ...prev[key], sub_prices: subs } }));
                            }}
                            className="w-32 text-sm"
                          />
                          <button
                            onClick={() => {
                              const subs = (pricing[key].sub_prices || []).filter((_, idx) => idx !== j);
                              setPricing(prev => ({ ...prev, [key]: { ...prev[key], sub_prices: subs } }));
                            }}
                            className="text-red-400 text-sm hover:text-red-600 shrink-0 px-1"
                          >x</button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const subs = [...(pricing[key].sub_prices || []), { name: '', price: 0 }];
                          setPricing(prev => ({ ...prev, [key]: { ...prev[key], sub_prices: subs } }));
                        }}
                        className="text-xs text-[#ffff00] hover:underline font-medium"
                        data-testid={`add-sub-price-${key}`}
                      >
                        + Agregar sub-precio
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <Button onClick={savePricing} disabled={savingPricing} className="w-full bg-[#ffff00] hover:bg-[#e6e600] text-[#000000]" data-testid="save-pricing-btn">
                {savingPricing ? 'Guardando...' : 'Guardar Precios'}
              </Button>
            </div>
          </div>
        )}

        {/* Servicios / Amenidades - Screenshot style */}
        {activeTab === 'amenities' && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold mb-1 text-[#000000]">Servicios de tu negocio</h2>
            <p className="text-sm text-gray-500 mb-6">Selecciona los servicios que ofrece tu negocio. Estos se mostrarán en tu perfil público.</p>

            <div className="space-y-6">
              {AMENITY_CATEGORIES.map(cat => (
                <div key={cat.name}>
                  <h3 className="text-sm font-bold text-[#000000] mb-3 uppercase tracking-wide">{cat.name}</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {cat.items.map(item => {
                      const Icon = AMENITY_ICON_MAP[item] || Home;
                      const isActive = amenities.includes(item);
                      return (
                        <button
                          key={item}
                          type="button"
                          onClick={() => toggleAmenity(item)}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                            isActive
                              ? 'border-[#ffff00] bg-[#ffff00]/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`amenity-${item}`}
                        >
                          <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-[#ffff00]' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${isActive ? 'text-[#000000]' : 'text-gray-600'}`}>{formatAmenity(item)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <Button onClick={saveAmenities} disabled={savingAmenities} className="w-full mt-6 bg-[#ffff00] hover:bg-[#e6e600] text-[#000000]" data-testid="save-amenities-btn">
              {savingAmenities ? 'Guardando...' : 'Guardar Servicios'}
            </Button>
          </div>
        )}

        {/* Galería */}
        {activeTab === 'gallery' && (
          <div className="space-y-6">
            <PremiumGallery isSubscribed={plan === 'premium_plus'} />
            <ProviderGallery providerId={provider.provider_id} />
          </div>
        )}

        {/* Redes Sociales */}
        {activeTab === 'social' && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold mb-1 text-[#000000]">Redes Sociales</h2>
            <p className="text-sm text-gray-500 mb-6">Agrega tus redes sociales para que aparezcan en tu perfil público.</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                <div className="relative">
                  <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input value={social.instagram} onChange={e => setSocial(prev => ({ ...prev, instagram: e.target.value }))} placeholder="https://instagram.com/tu-residencia" className="pl-11" data-testid="social-instagram" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                <div className="relative">
                  <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input value={social.facebook} onChange={e => setSocial(prev => ({ ...prev, facebook: e.target.value }))} placeholder="https://facebook.com/tu-residencia" className="pl-11" data-testid="social-facebook" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sitio Web</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input value={social.website} onChange={e => setSocial(prev => ({ ...prev, website: e.target.value }))} placeholder="https://www.tu-residencia.cl" className="pl-11" data-testid="social-website" />
                </div>
              </div>
              <Button onClick={saveSocial} disabled={savingSocial} className="w-full bg-[#ffff00] hover:bg-[#e6e600] text-[#000000]" data-testid="save-social-btn">
                {savingSocial ? 'Guardando...' : 'Guardar Redes Sociales'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderAccount;
